import React from 'react';
import { PivotTableData } from '../../models/PulseData';
import ReactMarkdown from 'react-markdown'; // Optional: if narrative is markdown
import PivotSankey from './PivotSankey';
import { formatUTCToLocalDisplayTime } from '../../utils/timeUtils';
import './PulsePage.css';

export const PIVOT_TABLE_CONTENT_ID = 'pivot-table-summary-content-area';

interface PivotTableSummaryProps {
  data: PivotTableData;
  title?: string; // Optional title for the pivot table
}

// Helper function to calculate percentage difference and determine trend bucket
const TREND_BUCKETS = [
  "T1_very_poor",
  "T2_poor",
  "T3_neutral",
  "T4_good",
  "T5_very_good"
];

const getTrend = (current: number, previous: number): number => {
  // Handle cases where previous value is zero
  let trendBucket: number = 2; // neutral

  if (previous === 0) {
    if (current > 0) {
      // Increase from zero - highest bucket (strong positive)
      trendBucket = 1; // Strong Increase
    } else {
      // 0 to 0 - no change
      trendBucket = 2; // No change
    }
  } else {

    const difference = ((current - previous) / previous) * 100;

    // Define buckets and corresponding yellow shades
    if (difference < -50) {
      trendBucket =  4; // Strong Decrease
    } else if (difference < -10) {
      trendBucket =  3; // Moderate Decrease
    } else if (difference >= -10 && difference <= 10) {
      trendBucket =  2; // No significant change
    } else if (difference > 10 && difference <= 50) {
      trendBucket =  1; // Moderate Increase
    } else { // difference > 50
      trendBucket =  0; // Strong Increase
    }
  }

  return trendBucket;
};

const PivotTableSummary: React.FC<PivotTableSummaryProps> = ({ data, title }) => {
  if (!data) {
    return <p>No pivot data available.</p>;
  }

  const { fieldNames, columnValues, rows, columnTotals, grandTotal, summary, narrative} = data;
  const tableTitle = title || `Error Counts by [${fieldNames.row}] and [${fieldNames.column}]`;

  const renderValue = ( row: any, col: string, colIndex: number) => {

    const currentValue = (row.valuesByColumn[col] !== undefined ? row.valuesByColumn[col] : 0);

    let trendValue = row.trendsByColumn ? row.trendsByColumn[col] : undefined;

    if (!trendValue) {

      let bgIndex = 2; // No change by default
      let previousValue = 0;

      if (colIndex > 0) {
        const previousCol = columnValues[colIndex - 1];
        previousValue = (row.valuesByColumn[previousCol] !== undefined ? row.valuesByColumn[previousCol] : 0);
        bgIndex = getTrend(currentValue, previousValue);
      }

      trendValue = TREND_BUCKETS[bgIndex];
    }

    return (
      <td key={`${row.rowValue}-${col}`} className={`count-cell pivot-trend-${trendValue}`}>
        {currentValue}
      </td>
    )
  }

  if (data.grandTotal === 0 && data.columnValues.length === 0) {
    return <p>No data available.</p>;
  }

  const MAX_VALUE_LENGTH = 200;

  const renderFieldValue = (fieldName: string, value: any ) => {

    const valueToRender = 
    ( value === "$deriq.other$"
      ? "Others (" + fieldName  + ")"
      : (value === "" 
        ? "(Empty)" 
        : typeof value === 'string' && (value as string).length > MAX_VALUE_LENGTH
        ? `${(value as string).substring(0, MAX_VALUE_LENGTH)} ...`
      : String(value ?? '')
      )
    );

    return valueToRender
  }

  return (
    <div id={PIVOT_TABLE_CONTENT_ID}>
      <PivotSankey data={data} title={tableTitle} /> {/* Add Sankey diagram here */}
      <hr/>
      <h3>{tableTitle}</h3>
      {(narrative && (summary.topn_percentage !== 100)) && (
        <div className="narrative-section">
          {/* <h4>Narrative:</h4> */}
          <ReactMarkdown>{narrative}</ReactMarkdown> {/* Or use <p>{narrative}</p> */}
        </div>
      )}
      <table className="pulse-table pivot-table">
        <thead>
          <tr>
            <th className="first-column">{fieldNames.row}</th>
            {columnValues.map(date => (
              // If 'date' is a YYYY-MM-DD string representing a UTC date,
              // parse it as such and then format it using UTC for display.
              <th key={date} className="count-cell">{formatUTCToLocalDisplayTime(date)}</th>              
            ))}
            <th className="total-cell">{fieldNames.rowTotal}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.rowValue}>
               <td className="first-column">
                {renderFieldValue(fieldNames.row, row.rowValue)}
              </td>
              {columnValues.map( (col, colIndex) => renderValue( row, col, colIndex))}
              <td className="total-cell">{row.rowTotal}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="totals-row">
            <td><strong>{fieldNames.columnTotal}</strong></td>
            {columnValues.map(date => (
              <td key={`total-${date}`} className="count-cell">
                <strong>{columnTotals[date] !== undefined ? columnTotals[date] : 0}</strong>
              </td>
            ))}
            <td className="total-cell">
              <strong>{grandTotal}</strong>
            </td>
          </tr>
        </tfoot>
      </table>
      {/* {summary && (
        <div className="attribute-metrics-section">
          <h4>Summary Metrics:</h4>
          <ul>
            {Object.entries(summary).map(([key, value]) => (
              <li key={key}>
                <span className="metric-key">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span>
                &nbsp;
                <span className="metric-value">{typeof value === 'number' && value % 1 !== 0 ? value.toFixed(2) : value}</span>
                {key.includes('percentage') && '%'}
              </li>
            ))}
          </ul>
        </div>
      )} */}
    </div>
  );
};

export default PivotTableSummary;